import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../config/AuthContext";
import { 
    Table, Button, Space, Avatar, Tag, Row, Col, Card, Tooltip, 
    Typography, message, Input, Modal, Switch, Select, Popconfirm, Divider, List, Checkbox
} from "antd";
import { 
    UserOutlined, SettingOutlined, SwapOutlined, 
    HomeOutlined, AppstoreOutlined, ArrowLeftOutlined, SearchOutlined, SafetyCertificateOutlined,
    UserSwitchOutlined
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import { GetUsuaris } from "../../../dao/GetUsuaris";
import { GetAllPages } from "../../../dao/GetAllPages";
import { GetAllAules } from "../../../dao/GetAllAules";
import HomeColors from "../../../styles/HomeColors";
import { DefaultAulumneInfo } from "../../../model/AlumneInfo";
import { GetAlumneId } from "../../../dao/GetAlumneId";

const { Title, Text } = Typography;

const AdminManagement = () => {
    const { isLoggedIn, userId, user, setBreadCrumb, setLoggedIn, setUserId, setUser, setUserPermisos, setAlumneInfo } = useContext(AuthContext);
    const navigate = useNavigate();
    
    const [usuaris, setUsuaris] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [filterRole, setFilterRole] = useState<number | null>(null);
    
    const [modalPages, setModalPages] = useState<any | null>(null);
    const [allPages, setAllPages] = useState<any[]>([]);
    const [userPages, setUserPages] = useState<number[]>([]);
    const [loadingPages, setLoadingPages] = useState(false);
    
    const [modalAules, setModalAules] = useState<any | null>(null);
    const [allAules, setAllAules] = useState<any[]>([]);
    const [userAules, setUserAules] = useState<number[]>([]);
    const [loadingAules, setLoadingAules] = useState(false);

    useEffect(() => {
        if (isLoggedIn && user.tipus === "0") {
            loadData();
            setBreadCrumb([
                { nom: 'Home', nav: '/home' },
                { nom: 'Admin Management', nav: '/home/admin' }
            ], true);
        } else if (isLoggedIn && user.tipus !== "0") {
            message.error("Access Denied");
            navigate("/home");
        }
    }, [isLoggedIn]);

    const loadData = async () => {
        setLoading(true);
        const [users, pages, aules] = await Promise.all([
            GetUsuaris(),
            GetAllPages(),
            GetAllAules("")
        ]);
        setUsuaris(users || []);
        setAllPages(pages || []);
        setAllAules(aules || []);
        setLoading(false);
    };

    const handleImpersonate = async (targetId: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}:8061/login/impersonate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ adminId: userId, targetId: targetId })
            });

            if (response.ok) {
                const data = await response.json();
                const { token, permisos } = data;

                let alu = DefaultAulumneInfo;
                if (permisos && permisos.length > 0) {
                    const res = await GetAlumneId(permisos[0].id, token);
                    if (res) alu = res;
                }

                if (!localStorage.getItem('adminToken')) {
                    localStorage.setItem('adminToken', localStorage.getItem('authToken') || '');
                    localStorage.setItem('admin_userId', localStorage.getItem('userId') || '');
                    localStorage.setItem('admin_user', localStorage.getItem('user') || '');
                    localStorage.setItem('admin_userPermisos', localStorage.getItem('userPermisos') || '');
                    localStorage.setItem('admin_alumneInfo', localStorage.getItem('alumneInfo') || '');
                }

                localStorage.setItem('authToken', token);
                setLoggedIn(true);
                setUserId(String(permisos[0].id));
                setUser(permisos[0]);
                setUserPermisos(permisos);
                setAlumneInfo(alu);

                message.success('Impersonando usuario...');
                window.location.reload();
            } else {
                message.error('Error al impersonar');
            }
        } catch (e) {
            message.error('Error de conexión');
        }
    };

    const handleRoleToggle = async (record: any) => {
        const newRole = record.rol === 1 ? 2 : 1;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}:8061/usuaris/${record.id}/rol`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role: newRole })
            });
            if (response.ok) {
                message.success(`Rol actualizado a ${newRole === 1 ? 'Admin' : 'Estudiant'}`);
                loadData();
            } else {
                message.error('Error al actualizar rol');
            }
        } catch (e) {
            message.error('Error de conexión');
        }
    };

    const openPagesModal = async (record: any) => {
        setModalPages(record);
        setLoadingPages(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}:8061/usuaris/${record.id}/pagines`);
            if (response.ok) {
                const data = await response.json();
                setUserPages(data.map((p: any) => p.id));
            }
        } catch (e) {
            message.error('No se pudieron cargar los permisos de página');
        }
        setLoadingPages(false);
    };

    const savePages = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}:8061/usuaris/${modalPages.id}/pagines`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pages: userPages })
            });
            if (response.ok) {
                message.success('Permisos actualizados');
                setModalPages(null);
            }
        } catch (e) {
            message.error('Error al guardar');
        }
    };

    const openAulesModal = async (record: any) => {
        setModalAules(record);
        setLoadingAules(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}:8061/usuaris/${record.id}/aules`);
            if (response.ok) {
                const data = await response.json();
                setUserAules(data.map((a: any) => a.id));
            }
        } catch (e) {
            message.error('No se pudieron cargar las aulas');
        }
        setLoadingAules(false);
    };

    const saveAules = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}:8061/usuaris/${modalAules.id}/aules`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ aules: userAules })
            });
            if (response.ok) {
                message.success('Aulas actualizadas');
                setModalAules(null);
            }
        } catch (e) {
            message.error('Error al guardar');
        }
    };

    const normalizeImg = (img?: string | null) => {
        if (!img || img === "undefined" || img === "null" || img === "") return "/images/default.png";
        if (img === "avatar1.jpg") return "/images/avatar1.jpg";
        if (img.startsWith("http") || img.startsWith("data:") || img.startsWith("/")) return img;
        if (img.endsWith(".jpg") || img.endsWith(".png") || img.endsWith(".jpeg") || img.endsWith(".svg")) return `/images/${img}`;
        return `data:image/png;base64,${img}`;
    };

    const filteredUsers = usuaris.filter(u => {
        const matchesSearch = searchText ? (
            u.nom?.toLowerCase().includes(searchText.toLowerCase()) || 
            u.usu?.toLowerCase().includes(searchText.toLowerCase()) ||
            u.email?.toLowerCase().includes(searchText.toLowerCase())
        ) : true;
        const matchesRole = filterRole !== null ? u.rol === filterRole : true;
        // Don't show super admins or at least don't allow modifying them
        return matchesSearch && matchesRole && u.id !== userId; 
    });

    const columns = [
        {
            title: 'Usuari',
            key: 'user',
            render: (_: any, record: any) => (
                <Space>
                    <Avatar src={normalizeImg(record.avatar)} icon={<UserOutlined />} />
                    <div>
                        <Text strong>{record.nom}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>@{record.usu}</Text>
                    </div>
                </Space>
            )
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (text: string) => <Text copyable>{text || '-'}</Text>
        },
        {
            title: 'Rol',
            dataIndex: 'rol',
            key: 'rol',
            width: 120,
            render: (rol: number) => {
                const config: any = {
                    0: { color: 'red', text: 'SuperAdmin' },
                    1: { color: 'gold', text: 'Admin' },
                    2: { color: 'blue', text: 'Estudiant' }
                };
                const c = config[rol] || { color: 'default', text: 'Alt' };
                return <Tag color={c.color} style={{ borderRadius: '8px' }}>{c.text}</Tag>;
            }
        },
        {
            title: 'Accions',
            key: 'actions',
            align: 'right' as const,
            render: (_: any, record: any) => (
                <Space>
                    <Tooltip title="Permisos de Pàgina">
                        <Button shape="circle" icon={<AppstoreOutlined />} onClick={() => openPagesModal(record)} />
                    </Tooltip>
                    
                    {record.rol === 1 && (
                        <Tooltip title="Gestionar Aules">
                            <Button shape="circle" icon={<HomeOutlined />} onClick={() => openAulesModal(record)} />
                        </Tooltip>
                    )}

                    <Tooltip title={record.rol === 1 ? "Convertir a Estudiant" : "Convertir a Admin"}>
                        <Popconfirm 
                            title={`Vols canviar el rol de ${record.nom}?`}
                            onConfirm={() => handleRoleToggle(record)}
                        >
                            <Button shape="circle" icon={<UserSwitchOutlined />} style={{ color: record.rol === 1 ? '#faad14' : '#1890ff' }} />
                        </Popconfirm>
                    </Tooltip>

                    <Tooltip title="Impersonar">
                        <Button 
                            shape="circle" 
                            icon={<SafetyCertificateOutlined />} 
                            onClick={() => handleImpersonate(record.id)}
                            style={{ color: '#52c41a' }}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <Card bordered={false} style={{ margin: '0 24px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <Space>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/home")} shape="circle" />
                        <Title level={3} style={{ margin: 0 }}>Gestió de Usuaris i Permisos</Title>
                    </Space>
                </Col>
                <Col>
                    <Space>
                        <Input 
                            placeholder="Cerca usuaris..." 
                            prefix={<SearchOutlined />} 
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: 250, borderRadius: '8px' }}
                        />
                        <Select
                            placeholder="Filtrar per rol"
                            allowClear
                            style={{ width: 150 }}
                            onChange={setFilterRole}
                            options={[
                                { label: 'Administradors', value: 1 },
                                { label: 'Estudiants', value: 2 }
                            ]}
                        />
                        <Button icon={<SafetyCertificateOutlined />} type="primary" onClick={loadData} style={{ borderRadius: '8px' }}>Refrescar</Button>
                    </Space>
                </Col>
            </Row>

            <Table 
                dataSource={filteredUsers} 
                columns={columns} 
                rowKey="id" 
                loading={loading}
                pagination={{ pageSize: 10 }}
                style={{ borderRadius: '12px', overflow: 'hidden' }}
            />

            {/* Modal Permisos Pàgina */}
            <Modal
                title={`Permisos de Pàgina - ${modalPages?.nom}`}
                open={!!modalPages}
                onCancel={() => setModalPages(null)}
                onOk={savePages}
                confirmLoading={loadingPages}
                width={600}
            >
                <Divider style={{ margin: '12px 0' }} />
                <List
                    dataSource={allPages}
                    renderItem={page => (
                        <List.Item>
                           <Checkbox 
                                checked={userPages.includes(page.id)}
                                disabled={page.titol === "Home" || page.titol === "Sortir"}
                                onChange={e => {
                                    if (e.target.checked) setUserPages([...userPages, page.id]);
                                    else setUserPages(userPages.filter(id => id !== page.id));
                                }}
                           >
                               <Space>
                                   <Text strong>{page.titol}</Text>
                                   <Text type="secondary" style={{ fontSize: '12px' }}>({page.accio})</Text>
                               </Space>
                           </Checkbox>
                        </List.Item>
                    )}
                />
            </Modal>

            {/* Modal Permisos Aules */}
            <Modal
                title={`Gestió de Aules - ${modalAules?.nom}`}
                open={!!modalAules}
                onCancel={() => setModalAules(null)}
                onOk={saveAules}
                confirmLoading={loadingAules}
                width={600}
            >
                <Divider style={{ margin: '12px 0' }} />
                <List
                    dataSource={allAules}
                    renderItem={aula => (
                        <List.Item>
                           <Checkbox 
                                checked={userAules.includes(aula.id)}
                                onChange={e => {
                                    if (e.target.checked) setUserAules([...userAules, aula.id]);
                                    else setUserAules(userAules.filter(id => id !== aula.id));
                                }}
                           >
                               <Space>
                                   <Avatar src={normalizeImg(aula.img)} size="small" shape="square" />
                                   <Text strong>{aula.nom}</Text>
                                   <Tag color="blue">ID: {aula.id}</Tag>
                               </Space>
                           </Checkbox>
                        </List.Item>
                    )}
                />
            </Modal>
        </Card>
    );
};

export default AdminManagement;
